import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GuardedLink as Link } from '../contexts/UnsavedChangesContext';
import './Sidebar.css';
import logo from '../assets/stema-whitelogo.png';
import { WORK_CATALOGUE_NAME } from '../constants/schema';

/* ─── Chevron SVG ───────────────────────────────────────────────────────────── */
function ChevronIcon({ isOpen }) {
  return (
    <svg
      className={`nav-chevron${isOpen ? ' open' : ''}`}
      width="11" height="11" viewBox="0 0 12 12"
      fill="none" xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2 4.5L6 8.5L10 4.5"
        stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/* ─── Collapsible section ───────────────────────────────────────────────────── */
function NavSection({ id, title, children, isOpen, onToggle, sidebarCollapsed }) {
  // Icon-only mode: skip headers, show all items flat
  if (sidebarCollapsed) {
    return (
      <li className="nav-section nav-section--icon-only" role="none">
        <ul className="nav-section-items" role="none">
          {children}
        </ul>
      </li>
    );
  }

  return (
    <li className="nav-section" role="none">
      {title && (
        <button
          className="nav-section-header"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={`nav-section-${id}`}
        >
          <span className="nav-section-label">{title}</span>
          <ChevronIcon isOpen={isOpen} />
        </button>
      )}

      {/* CSS grid trick: grid-template-rows animates 0fr → 1fr smoothly */}
      <div
        id={`nav-section-${id}`}
        className={`nav-section-body${isOpen ? ' open' : ''}`}
        role="none"
      >
        <ul className="nav-section-items" role="none">
          {children}
        </ul>
      </div>
    </li>
  );
}

/* ─── Single nav item ───────────────────────────────────────────────────────── */
function NavItem({ to, icon, label, badge, isActive, collapsed, onClick, iconClass }) {
  return (
    <li>
      <Link to={to} className={isActive ? 'active' : ''} title={label} onClick={onClick}>
        <span className={`nav-icon${iconClass ? ` ${iconClass}` : ''}`}>{icon}</span>
        {!collapsed && (
          <span className="nav-text">
            {label}
            {badge && <span className="nav-coming-soon">{badge}</span>}
          </span>
        )}
      </Link>
    </li>
  );
}

/* ─── Map paths → section IDs (for auto-expand) ────────────────────────────── */
const PATH_TO_SECTION = {
  '/': 'core',
  '/dashboard': 'core',
  '/follow-ups': 'core',
  '/material-catalogue': 'inventory',
  '/works-catalogue': 'inventory',
  '/purchasing': 'inventory',
  '/inventory': 'inventory',
  '/sales-invoices': 'projects',
  '/project-costing': 'projects',
  '/attendance': 'operations',
  '/claims': 'operations',
  // Renders inside INVENTORY & MATERIALS, so it must auto-expand that section —
  // this said 'operations' and quietly opened the wrong one.
  '/general-expenses': 'inventory',
  '/daily-transport': 'operations',
  '/defect-tracker': 'operations',
  '/team-management': 'operations',
  '/projects': 'admin',
  '/supplier-list': 'admin',
  '/sop': 'admin',
};

function getActiveSectionId(pathname) {
  if (pathname === '/') return 'core';
  const match = Object.keys(PATH_TO_SECTION).find(
    p => p !== '/' && (pathname === p || pathname.startsWith(p + '/'))
  );
  return match ? PATH_TO_SECTION[match] : 'core';
}

/* ─── Nav memory: remember the last path visited within each section ─────────
   Base paths (the sidebar link targets) double as section keys. When a user
   drills into a detail route (e.g. /project-costing/:id) and later clicks the
   sidebar link, we send them back to where they left off instead of the base
   landing page. Persisted per browser session (sessionStorage). Home ('/') is
   excluded — it has no detail view to remember. */
const NAV_MEMORY_PREFIX = 'navMemory:';
const NAV_BASE_PATHS = Object.keys(PATH_TO_SECTION).filter(p => p !== '/');

function matchNavBasePath(pathname) {
  return NAV_BASE_PATHS.find(
    base => pathname === base || pathname.startsWith(base + '/')
  ) || null;
}

/* ─── Sidebar ───────────────────────────────────────────────────────────────── */
function Sidebar({ role, isCollapsed, onToggle, isMobileOpen, onMobileToggle, onMobileClose, presentationMode, catalogueAccessLevels }) {
  const [user, setUser] = useState(null);
  const location = useLocation();

  // Open core + whichever section the current page lives in
  // All sections open by default on every load
  const [openSections, setOpenSections] = useState(
    () => new Set(['core', 'inventory', 'projects', 'operations', 'admin'])
  );

  // Auto-expand when navigating into a collapsed section
  useEffect(() => {
    const active = getActiveSectionId(location.pathname);
    setOpenSections(prev => {
      if (prev.has(active)) return prev;
      const next = new Set(prev);
      next.add(active);
      return next;
    });
  }, [location.pathname]);

  // Remember the last full path visited within each section, seeded from and
  // persisted to sessionStorage (see NAV_MEMORY_PREFIX above).
  const [navMemory, setNavMemory] = useState(() => {
    const initial = {};
    try {
      NAV_BASE_PATHS.forEach(base => {
        const stored = sessionStorage.getItem(NAV_MEMORY_PREFIX + base);
        if (stored) initial[base] = stored;
      });
    } catch (err) {
      console.warn('Failed to load nav memory from sessionStorage:', err);
    }
    return initial;
  });

  useEffect(() => {
    const base = matchNavBasePath(location.pathname);
    if (!base) return;
    const fullPath = location.pathname + location.search;
    setNavMemory(prev => {
      if (prev[base] === fullPath) return prev;
      try {
        sessionStorage.setItem(NAV_MEMORY_PREFIX + base, fullPath);
      } catch (err) {
        console.warn('Failed to save nav memory to sessionStorage:', err);
      }
      return { ...prev, [base]: fullPath };
    });
  }, [location.pathname, location.search]);

  // Resolve a sidebar link's base path to the last-visited path in that section.
  const resolveTo = (base) => navMemory[base] || base;

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (err) {
      console.warn('Failed to load user from sessionStorage:', err);
    }
  }, []);

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSection = useCallback((id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  /* ── Access levels ──
     All default to 0, never 2. A nav item is a link to a page the route guards
     will bounce them from anyway; showing it because the level hasn't loaded
     (or because a feature was never added to the derived map) is how modules
     ended up visible to people set to No Access. Fail closed here too. */
  const mcAccess          = catalogueAccessLevels?.material_catalogue  ?? 0;
  const wcAccess          = catalogueAccessLevels?.works_catalogue     ?? 0;
  const sopAccess         = catalogueAccessLevels?.sop                 ?? 0;
  const dtAccess          = catalogueAccessLevels?.daily_transport     ?? 0;
  const defectAccess      = catalogueAccessLevels?.defect_tracker      ?? 0;
  const teamAccess        = catalogueAccessLevels?.team_management     ?? 0;
  const projectsAccess    = catalogueAccessLevels?.projects            ?? 0;
  const supplierAccess    = catalogueAccessLevels?.supplier_list       ?? 0;
  const projectCostAccess = catalogueAccessLevels?.project_costing     ?? 0;
  const attendanceAccess  = catalogueAccessLevels?.attendance          ?? 0;
  const genExpAccess      = catalogueAccessLevels?.general_expenses    ?? 0;
  const purchasingAccess  = catalogueAccessLevels?.follow_ups          ?? 0;
  const inventoryAccess   = catalogueAccessLevels?.inventory           ?? 0;
  const followUpsAccess   = catalogueAccessLevels?.project_follow_ups  ?? 0;
  const salesAccess       = catalogueAccessLevels?.sales_invoices      ?? 0;
  const claimsAccess      = catalogueAccessLevels?.claims              ?? 0;

  const isSupplierRole = role === 'admin' || role === 'boss' || role === 'bosscontrol';

  const handleLinkClick = () => {
    if (window.innerWidth <= 768 && onMobileClose) onMobileClose();
  };

  /* ── Section visibility guards ── */
  const showInventory = mcAccess > 0 || wcAccess > 0
    || (!presentationMode && (purchasingAccess > 0 || inventoryAccess > 0 || genExpAccess > 0));
  // Both sections used to render unconditionally, which left an empty section
  // header for anyone whose items were all gated away.
  const showProjects  = !presentationMode && (salesAccess > 0 || projectCostAccess > 0);
  const showOps       = !presentationMode && (
    attendanceAccess > 0 || claimsAccess > 0 || dtAccess > 0 ||
    defectAccess > 0 || teamAccess > 0
  );
  const showAdmin     = !presentationMode && (
    teamAccess > 0 || sopAccess > 0 || projectsAccess > 0 ||
    (supplierAccess > 0 && isSupplierRole)
  );

  const mk = (id) => ({
    id,
    isOpen: openSections.has(id),
    onToggle: () => toggleSection(id),
    sidebarCollapsed: isCollapsed,
  });

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        {!isCollapsed && <img src={logo} alt="STEMA Logo" />}
        {isCollapsed  && <div className="logo-icon">S</div>}
      </div>

      <nav aria-label="Main navigation">
        <ul className="nav-root" role="list">

          {/* ── CORE NAVIGATION ── */}
          {!presentationMode && (
            <NavSection title="CORE NAVIGATION" {...mk('core')}>
              <NavItem to="/" icon="🏠" label="Home"
                isActive={isActive('/')} collapsed={isCollapsed} onClick={handleLinkClick} />
              {(role === 'bosscontrol' || role === 'accountant') && (
                <NavItem to={resolveTo('/dashboard')} icon="📊" label="Dashboard" badge="Soon"
                  isActive={isActive('/dashboard')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {followUpsAccess > 0 && (
                <NavItem to={resolveTo('/follow-ups')} icon="✅" label="Follow Ups" badge="Soon"
                  isActive={isActive('/follow-ups')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
            </NavSection>
          )}

          {/* ── INVENTORY & MATERIALS ── */}
          {showInventory && (
            <NavSection title="INVENTORY & MATERIALS" {...mk('inventory')}>
              {mcAccess > 0 && (
                <NavItem to={resolveTo('/material-catalogue')} icon="🔧"
                  label="Material & Ironmongery Catalogue"
                  isActive={isActive('/material-catalogue')}
                  collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {wcAccess > 0 && (
                <NavItem to={resolveTo('/works-catalogue')} icon="🗃️"
                  label={WORK_CATALOGUE_NAME}
                  isActive={isActive('/works-catalogue')}
                  collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {!presentationMode && purchasingAccess > 0 && (
                <NavItem to={resolveTo('/purchasing')} icon="🛒" label="Purchasing"
                  isActive={isActive('/purchasing')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {!presentationMode && genExpAccess > 0 && (
                <NavItem to={resolveTo('/general-expenses')} icon="🧰" label="General Expenses"
                  isActive={isActive('/general-expenses')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {!presentationMode && inventoryAccess > 0 && (
                <NavItem to={resolveTo('/inventory')} icon="📦" label="Inventory"
                  isActive={isActive('/inventory')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
            </NavSection>
          )}

          {/* ── PROJECT MANAGEMENT ── */}
          {showProjects && (
            <NavSection title="PROJECT MANAGEMENT" {...mk('projects')}>
              {salesAccess > 0 && (
                <NavItem to={resolveTo('/sales-invoices')} icon="💰" label="Quotation & Invoice"
                  isActive={isActive('/sales-invoices')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {projectCostAccess > 0 && (
                <NavItem to={resolveTo('/project-costing')} icon="📊" label="Project Costing"
                  isActive={isActive('/project-costing')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
            </NavSection>
          )}

          {/* ── OPERATIONS & HR ── */}
          {showOps && (
            <NavSection title="OPERATIONS & HR" {...mk('operations')}>
              {attendanceAccess > 0 && (
                <NavItem to={resolveTo('/attendance')} icon="🕐" label="Attendance" badge="Soon"
                  isActive={isActive('/attendance')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {claimsAccess > 0 && (
                <NavItem to={resolveTo('/claims')} icon="🧾" label="Staff Claim" badge="Soon"
                  isActive={isActive('/claims')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {dtAccess > 0 && (
                <NavItem to={resolveTo('/daily-transport')} icon="🚚" label="Daily Transport"
                  isActive={isActive('/daily-transport')} iconClass="truck-icon"
                  collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {defectAccess > 0 && (
                <NavItem to={resolveTo('/defect-tracker')} icon="⚠️" label="Defect Tracker" badge="Soon"
                  isActive={isActive('/defect-tracker')} iconClass="defect-icon"
                  collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {teamAccess > 0 && (
                <NavItem to={resolveTo('/team-management')} icon="👥" label="Team Management"
                  isActive={isActive('/team-management')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
            </NavSection>
          )}

          {/* ── COMPANY ADMINISTRATION ── */}
          {showAdmin && (
            <NavSection title="COMPANY ADMINISTRATION" {...mk('admin')}>
              {projectsAccess > 0 && (
                <NavItem to={resolveTo('/projects')} icon="🗂️" label="Project List"
                  isActive={isActive('/projects')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {supplierAccess > 0 && isSupplierRole && (
                <NavItem to={resolveTo('/supplier-list')} icon="📇" label="Supplier List"
                  isActive={isActive('/supplier-list')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
              {sopAccess > 0 && (
                <NavItem to={resolveTo('/sop')} icon="📝" label="SOP"
                  isActive={isActive('/sop')} collapsed={isCollapsed} onClick={handleLinkClick} />
              )}
            </NavSection>
          )}

        </ul>
      </nav>

      <div className="sidebar-toggle" onClick={onToggle} role="button" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        <div className={`toggle-arrow ${isCollapsed ? 'collapsed' : ''}`}>
          <div className="arrow-line" />
          <div className="arrow-line" />
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
